import type { MitreState } from '../mitre-attck/useMitreData';
import type {
  WazuhMitreAgentSummaryData,
  WazuhMitreDetectionsBySeverity,
  WazuhMitreTechniqueItem,
} from 'src/types/mindtime/wazuhMitre';
import { previewPaged } from './previewUtils';

export const PREVIEW_MITRE_IDS = ['preview-mitre-1', 'preview-mitre-2'] as const;

const PREVIEW_TECHNIQUES: WazuhMitreTechniqueItem[] = [
  {
    id: 'preview-mitre-1',
    mitreId: 'T1059',
    techniqueName: 'Command and Scripting Interpreter',
    tactic: 'Execution',
    tactics: ['Execution'],
    detections: 24,
    severity: 'high',
    affectedEndpointCount: 6,
    endpoint: 'FIN-SRV-02',
    organization: 'Acme Financial',
    systemWazuhAgentId: 'preview-endpoint-1',
    timestamp: '2026-08-28T10:15:00.000Z',
    hostName: 'fin-srv-02.acme.local',
  },
  {
    id: 'preview-mitre-2',
    mitreId: 'T1003',
    techniqueName: 'OS Credential Dumping',
    tactic: 'Credential Access',
    tactics: ['Credential Access'],
    detections: 12,
    severity: 'critical',
    affectedEndpointCount: 3,
    endpoint: 'HR-PC-17',
    organization: 'Vertex Solutions',
    systemWazuhAgentId: 'preview-endpoint-2',
    timestamp: '2026-08-28T09:42:00.000Z',
    hostName: 'hr-pc-17.vertex.local',
  },
  {
    id: 'preview-mitre-3',
    mitreId: 'T1021',
    techniqueName: 'Remote Services',
    tactic: 'Lateral Movement',
    tactics: ['Lateral Movement'],
    detections: 8,
    severity: 'medium',
    affectedEndpointCount: 4,
    endpoint: 'APP-SERVER-01',
    organization: 'Nexus Dynamics',
    systemWazuhAgentId: 'preview-endpoint-3',
    timestamp: '2026-08-27T18:20:00.000Z',
    hostName: 'app-server-01.nexus.local',
  },
];

const buildPreviewSummary = (items: WazuhMitreTechniqueItem[]): WazuhMitreAgentSummaryData => {
  const detectionsBySeverity: WazuhMitreDetectionsBySeverity = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  let totalDetections = 0;
  let highRiskTechniques = 0;

  items.forEach((item) => {
    const count = item.detections ?? 0;
    totalDetections += count;
    const key = (item.severity ?? '').trim().toLowerCase();
    if (key === 'critical' || key === 'high' || key === 'medium' || key === 'low') {
      detectionsBySeverity[key] = (detectionsBySeverity[key] ?? 0) + count;
    }
    if (key === 'critical' || key === 'high') {
      highRiskTechniques += 1;
    }
  });

  return {
    activeTechniques: items.length,
    highRiskTechniques,
    totalDetections,
    detectionsBySeverity,
  };
};

export const getMitrePreviewState = (): MitreState => ({
  loading: false,
  summary: {
    activeTechniques: 18,
    highRiskTechniques: 6,
    totalDetections: 64,
    detectionsBySeverity: { critical: 8, high: 22, medium: 24, low: 10 },
  },
  items: previewPaged(PREVIEW_TECHNIQUES, 0, 15),
  endpoints: previewPaged(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-1',
        endpoint: 'FIN-SRV-02',
        hostName: 'fin-srv-02.acme.local',
        organization: 'Acme Financial',
        activeTechniques: 4,
        highRiskTechniques: 2,
        totalDetections: 24,
        detectionsBySeverity: { critical: 4, high: 10, medium: 8, low: 2 },
      },
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        activeTechniques: 3,
        highRiskTechniques: 1,
        totalDetections: 12,
        detectionsBySeverity: { critical: 3, high: 5, medium: 3, low: 1 },
      },
      {
        systemWazuhAgentId: 'preview-endpoint-3',
        endpoint: 'APP-SERVER-01',
        hostName: 'app-server-01.nexus.local',
        organization: 'Nexus Dynamics',
        activeTechniques: 2,
        highRiskTechniques: 0,
        totalDetections: 8,
        detectionsBySeverity: { critical: 0, high: 2, medium: 4, low: 2 },
      },
    ],
    0,
    10,
  ),
  topTechniques: PREVIEW_TECHNIQUES.slice(0, 3),
  facets: {
    tactics: ['Execution', 'Credential Access', 'Lateral Movement', 'Persistence'],
    techniques: ['T1059', 'T1003', 'T1021', 'T1053'],
    mitreIds: ['T1059', 'T1003', 'T1021', 'T1053'],
  },
});

export const getMitreDetailPreview = (id: string): WazuhMitreTechniqueItem | null =>
  PREVIEW_TECHNIQUES.find((row) => row.id === id || row.mitreId === id) ?? PREVIEW_TECHNIQUES[0] ?? null;

export const getAgentMitrePreview = (
  systemWazuhAgentId: string,
): { summary: WazuhMitreAgentSummaryData; items: WazuhMitreTechniqueItem[] } => {
  const agentId = systemWazuhAgentId.trim();
  const items = PREVIEW_TECHNIQUES.filter(
    (row) => row.systemWazuhAgentId === agentId || row.wazuhAgentId === agentId,
  );
  const list = items.length ? items : PREVIEW_TECHNIQUES.slice(0, 1);

  return {
    summary: {
      systemWazuhAgentId: agentId,
      ...buildPreviewSummary(list),
    },
    items: list,
  };
};
